import React, { FC, useEffect, useMemo } from 'react';
import cls from './Table.module.scss';
import PageDataLoader from 'src/components/UI/Loaders/PageDataLoader/PageDataLoader';

import TableHeader from './components/TableHeader';
import useSearchQueryParams from 'src/hooks/useSearchQueryParams';
import { useTranslation } from 'react-i18next';
import { defaultTableName } from './Table';
import { setTableDataIds } from 'src/redux/slices/selectedRows';
import { useAppDispatch } from 'src/redux';
import { SortFieldTypes, TableProps } from './types';
import TableRow from './components/TableRow';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import selectors from 'src/redux/selectors';
import TrRender from './components/TrRender';
type DragEvent = React.DragEvent<HTMLTableRowElement>;

interface TableNoVirtualProps extends TableProps {
  isDraggable?: boolean;
  onDragStart?: (idx: number) => (e: DragEvent) => void;
  onDragOver?: (e: DragEvent) => void;
  onDragLeave?: (e: DragEvent) => void;
  onDrop?: (idx: number) => (e: DragEvent) => void;
}
const TableNoVirtual: FC<TableNoVirtualProps> = ({
  config,
  tableData,
  filterOptions,
  className = '',
  disableNoWrap,
  tableRef,
  isDraggable,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  dataLoading,
  rowHeight,
}: TableNoVirtualProps) => {
  const dispatch = useAppDispatch();
  const { params } = useSearchQueryParams({});
  const { t: tg } = useTranslation();
  useEffect(() => {
    dispatch(setTableDataIds(tableData.map((item) => item.data.id)));
  }, [tableData]);
  const actionsNumber = useMemo(() => {
    const hasEdit = tableData.find((item) => item.data.onEdit);
    const hasDelete = tableData.find((item) => item.data.onEdit);
    return 0 + (hasEdit ? 1 : 0) + (hasDelete ? 1 : 0);
  }, [tableData]);
  const sortFields = useTypedSelector(selectors.getSortFields);
  const tableDataSorted = useMemo(() => {
    const sortField = sortFields?.[config.tableName || defaultTableName];
    if (!config.hasSort || !sortField) return tableData;
    const result = [...tableData].sort((a, b) => {
      const aData = a?.elements[sortField.columnId]?.sortValue;
      const bData = b?.elements[sortField.columnId]?.sortValue;
      let res = 0;
      if (!aData) {
        return 1;
      }
      if (!bData) {
        return -1;
      }
      if (aData < bData) {
        res = -1;
      } else if (aData > bData) {
        res = 1;
      }
      return sortField.type === SortFieldTypes.ASC ? res : res * -1;
    });
    return result;
  }, [tableData, config.hasSort, sortFields]);

  return (
    <div
      className={`${cls['table-wrapper']} ${className} ${cls['table']} ${
        disableNoWrap && cls['table-disable-nowrap']
      }`}
      ref={tableRef}
    >
      <table>
        <thead>
          <TableHeader
            config={config}
            tableData={tableData}
            filterOptions={filterOptions}
            actionsNumber={actionsNumber}
          />
        </thead>
        <tbody>
          {
            dataLoading ? (
              <td colSpan={config.th.length}>
                <PageDataLoader />
              </td>
            ) : !tableData.length ? (
              <tr className="table__tr-no-data">
                <td colSpan={config.th.length + (config.hasSelection ? 1 : 0)}>
                  {Object.values(params || {}).length
                    ? tg('table-no-data-filter')
                    : tg('table-no-data-new')}
                </td>
              </tr>
            ) : (
              tableDataSorted.map((tableDataItem, tableDataIndex) => (
                <TrRender
                  tableDataSorted={tableDataSorted}
                  key={tableDataItem.data.id}
                  props={{
                    draggable: isDraggable,
                    onDragStart: onDragStart && onDragStart(tableDataIndex),
                    onDragOver: onDragOver && onDragOver,
                    onDragLeave: onDragLeave && onDragLeave,
                    onDrop: onDrop && onDrop(tableDataIndex),
                    'data-index': tableDataIndex,
                    children: (
                      <TableRow
                        config={config}
                        tableDataItem={tableDataItem}
                        hasActions={!!actionsNumber}
                      />
                    ),
                  }}
                />
              ))
            )
            // tableData.map( (tableDataItem, index) => {
            //     const tableDataStyle = rowHeight !== 'disabled' ? {height: rowHeight || baseRowHeight}:{};
            //     return (
            //       <tr
            //         onDoubleClick={() => {
            //           tableDataItem.data.onEdit
            //             ? tableDataItem.data.onEdit()
            //             : null;
            //         }}
            //         onClick={(event) => {
            //           tableDataItem.data.onClick
            //             ? tableDataItem.data.onClick()
            //             : dispatch(
            //               toggleSelectedRows({
            //                 value: tableDataItem.data.id,
            //                 event: {
            //                   altKey: event.altKey,
            //                   shiftKey: event.shiftKey
            //                 }
            //               })
            //             )
            //         }}
            //         key={index}
            //         className={`table__tr-data table__tr-data--first  ${
            //           tableDataItem.data?.isDisabled && 'table__tr-data--disabled'
            //         }`}
            //         style={tableDataStyle}
            //         draggable={isDraggable}
            //         onDragStart={onDragStart && onDragStart(index)}
            //         onDragOver={onDragOver && onDragOver}
            //         onDragLeave={onDragLeave && onDragLeave}
            //         onDrop={onDrop && onDrop(index)}
            //       >
            //         {config.th.map((thConfig, thConfigIndex) => (
            //           <TableDataElement
            //             key={thConfigIndex}
            //             thConfig={thConfig}
            //             tableDataItem={tableDataItem}
            //             thConfigIndex={thConfigIndex}
            //           />
            //         ))}
            //       </tr>
            //     )
            //   })
          }
        </tbody>
      </table>
    </div>
  );
};
export default TableNoVirtual;
